import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { momentService } from '../../../services/momentService';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { MomentTimeline } from '../../../components/app/moments/MomentTimeline';
import { MomentModals } from '../../../components/app/moments/MomentModals';
import { ReflectionPanel } from '../../../components/app/moments/ReflectionPanel';
import { MomentSearch } from '../../../components/app/moments/MomentSearch';
import { MomentRediscover } from '../../../components/app/moments/MomentRediscover';
import { TrashView } from '../../../components/app/moments/TrashView';
import { useMomentEditor } from '../../../components/app/moments/useMomentEditor';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const EMPTY_FILTERS = { q: '', contactId: '', originId: '', from: '', to: '', sort: 'newest' };

const uniqueBy = (list) => Array.from(new Map(list.map((x) => [x.id, x])).values());

export const MomentsList = () => {
  const { user } = useAuth();
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [view, setView] = useState('moments');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const editor = useMomentEditor({ user, setMoments });
  const { success, warning, setFormState, setViewing } = editor;

  const active = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  useEffect(() => {
    if (!user?.id) return;
    momentService.getMoments()
      .then(setMoments)
      .catch((err) => setLoadError(err.message || 'Could not load your moments.'))
      .finally(() => setLoading(false));
  }, [user?.id]);

  // Re-runs when filters change or when Moments are added, edited or trashed.
  useEffect(() => {
    if (!active) {
      setResults(null);
      setSearchError('');
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    momentService.searchMoments(filters)
      .then((rows) => { if (!cancelled) { setResults(rows); setSearchError(''); } })
      .catch((err) => { if (!cancelled) setSearchError(err.message || 'Search failed.'); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [filters, moments, active]);

  const people = useMemo(
    () => uniqueBy(moments.flatMap((m) => m.people || [])).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')),
    [moments]
  );
  const origins = useMemo(
    () => uniqueBy(moments.map((m) => m.origin).filter(Boolean)).sort((a, b) => a.label.localeCompare(b.label)),
    [moments]
  );

  const reload = async () => {
    setMoments(await momentService.getMoments());
  };

  const pick = (partial) => setFilters((f) => ({ ...f, ...partial }));

  if (loading) return <div className="flex justify-center py-32"><Spinner size="lg" /></div>;

  const shown = results ?? moments;

  return (
    <div className="max-w-3xl pb-16">
      <MomentModals editor={editor} moments={moments} />

      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Moments</h1>
          <p className="mt-2 text-[15px] font-medium text-slate-500">Experiences worth remembering, private to you.</p>
        </div>
        <Button onClick={() => setFormState({ moment: null })}>
          <SafeIcon icon={FiIcons.FiPlus} className="mr-2" /> Add moment
        </Button>
      </header>

      <div className="mb-6 inline-flex rounded-full border border-slate-200 bg-white p-1">
        {[['moments', 'Moments', FiIcons.FiFeather], ['trash', 'Trash', FiIcons.FiTrash2]].map(([key, label, icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${view === key ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-800'}`}
          >
            <SafeIcon icon={icon} /> {label}
          </button>
        ))}
      </div>

      {success && <Alert variant="success" message={success} className="mb-6" />}
      {warning && <Alert variant="error" title="Saved with a problem" message={warning} className="mb-6" />}
      {loadError && <Alert variant="error" title="Could not load moments" message={loadError} className="mb-6" />}

      {view === 'trash' ? (
        <TrashView onRestored={reload} />
      ) : (
        <>
          {moments.length > 0 && (
            <>
              <MomentSearch
                filters={filters}
                setFilters={setFilters}
                people={people}
                origins={origins}
                active={active}
                onClear={() => setFilters(EMPTY_FILTERS)}
              />
              {!active && <MomentRediscover moments={moments} onPick={pick} />}
              {!active && <ReflectionPanel moments={moments} onOpenMoment={setViewing} />}
            </>
          )}

          {searchError && <Alert variant="error" title="Search problem" message={searchError} className="mb-6" />}
          {active && results && (
            <p className="mb-4 text-sm font-medium text-slate-500">
              {searching ? 'Searching…' : `${results.length} moment${results.length === 1 ? '' : 's'} found`}
            </p>
          )}

          {moments.length === 0 && !loadError ? (
            <div className="bg-white rounded-[2rem] border border-slate-200/60 border-dashed p-12 text-center shadow-sm">
              <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-slate-100">
                <SafeIcon icon={FiIcons.FiFeather} className="text-2xl text-slate-400" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 tracking-tight">No moments yet</h3>
              <p className="text-[15px] font-medium text-slate-500 max-w-sm mx-auto mb-6">
                Capture a conversation, an encounter or a feeling while it is still fresh.
              </p>
              <Button onClick={() => setFormState({ moment: null })}>Capture your first moment</Button>
            </div>
          ) : active && results && results.length === 0 && !searching ? (
            <div className="bg-white rounded-[2rem] border border-slate-200/60 border-dashed p-10 text-center shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-2 tracking-tight">No moments match</h3>
              <p className="text-[15px] font-medium text-slate-500 mb-5">Try fewer words or a wider date range.</p>
              <Button variant="secondary" onClick={() => setFilters(EMPTY_FILTERS)}>Clear filters</Button>
            </div>
          ) : (
            <MomentTimeline moments={shown} onOpen={setViewing} />
          )}
        </>
      )}
    </div>
  );
};